import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Home, BarChart2, FileText, Table, Clock, Coins, Settings, UserCircle2, Sparkles,
  Building2, Inbox, Lock, CreditCard, BadgeCheck, LifeBuoy, LogOut, Menu, X, Wallet, Store, TrendingUp, AlertTriangle, Receipt, Search, Crown,
} from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import api from "@/lib/api";
import { AskModal } from "@/components/ai/AskModal";
import { DataProvider } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { section: "Command", items: [{ to: "/home", label: "Command Center", icon: Home, end: true }] },
  { section: "Funding Readiness", items: [
    { to: "/funding-readiness", label: "Readiness Diagnostic", icon: BarChart2 },
    { to: "/establish-credit", label: "Establish Credit", icon: FileText },
    { to: "/business-credit", label: "Business Credit", icon: BadgeCheck },
    { to: "/credit-reports", label: "Credit Reports", icon: FileText },
    { to: "/trade-lines", label: "Tradelines", icon: Table },
    { to: "/rent-bill-reporting", label: "Rent & Bill Reporting", icon: Receipt },
    { to: "/cash-flow", label: "Cash Flow", icon: Clock },
  ]},
  { section: "Capital & Products", items: [
    { to: "/funding-options", label: "Funding Options", icon: Coins },
    { to: "/credit-builder", label: "Credit Builder", icon: TrendingUp },
    { to: "/documents", label: "Documents", icon: FileText },
  ]},
  { section: "Marketplace", items: [
    { to: "/funding-options", label: "Loans", icon: Wallet, key: "loans" },
    { to: "/funding-options", label: "Credit cards", icon: CreditCard, key: "credit-cards" },
    { to: "/trade-lines", label: "Net-30+ vendors", icon: Store, key: "net-30-vendors" },
  ]},
];

const PLAN_TONE = { Preview: "text-slate-500 border-slate-200 bg-slate-50", Track: "text-[#4338CA] border-[#C7D2FE] bg-[#F1F0FB]", Build: "text-[#4338CA] border-[#5B4BE6]/40 bg-[#F1F0FB]", Expand: "text-emerald-700 border-emerald-200 bg-emerald-50" };

function NavItems({ onNavigate }) {
  return (
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4" data-testid="member-shell-sidebar">
      {NAV.map((g) => (
        <div key={g.section}>
          <div className="px-3 pb-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#5B4BE6]">{g.section}</div>
          <div className="space-y-0.5">
            {g.items.map((item) => (
              <NavLink key={item.key || item.to} to={item.to} end={item.end} onClick={onNavigate}
                data-testid={item.key ? `cust-market-${item.key}` : `cust-nav-${item.to.replace("/", "")}`}
                className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${isActive && !item.key ? "bg-[#F1F0FB] text-[#4338CA]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                <item.icon size={16} className="shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function AccountMenu({ user, logout, navigate, trigger, align = "end" }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56 border-slate-200 bg-white text-slate-700">
        <DropdownMenuLabel className="truncate">{user?.company_name || "My Business"}</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-100" />
        <DropdownMenuItem onClick={() => navigate("/business-identity")} data-testid="acct-profile"><Building2 size={15} className="mr-2" /> Business Profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/capital-passport")}><BadgeCheck size={15} className="mr-2" /> Capital Passport</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/approvals")} data-testid="acct-approvals"><Inbox size={15} className="mr-2" /> Approvals &amp; Consent</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/billing")}><CreditCard size={15} className="mr-2" /> Membership &amp; Billing</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/privacy")}><Lock size={15} className="mr-2" /> Privacy &amp; Settings</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/info/help-center")}><LifeBuoy size={15} className="mr-2" /> Support</DropdownMenuItem>
        <DropdownMenuSeparator className="bg-slate-100" />
        <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="cust-logout"><LogOut size={15} className="mr-2" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Inner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [askOpen, setAskOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [planInfo, setPlanInfo] = useState({ plan: null, status: null });

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setAskOpen((v) => !v); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    api.get("/payments/my-plan").then(({ data }) => setPlanInfo({ plan: data.plan || "Preview", status: data.subscription_status })).catch(() => {});
  }, []);
  const pastDue = planInfo.status === "past_due";

  return (
    <div className="font-inter flex h-screen overflow-hidden bg-[#F8FAFC] text-slate-700" data-testid="customer-portal">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex h-16 items-center gap-3 border-b border-slate-200 px-5"><Logo height={22} variant="dark" /></div>
        <NavItems />
        <div className="border-t border-slate-200 p-3">
          <AccountMenu user={user} logout={logout} navigate={navigate} align="start"
            trigger={
              <button data-testid="cust-account-settings" className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900">
                <Settings size={16} /> <span>Account &amp; Settings</span>
              </button>
            } />
        </div>
      </aside>

      {mobileNav && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileNav(false)} />
          <div className="absolute left-0 top-0 flex h-full w-64 flex-col border-r border-slate-200 bg-white">
            <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5"><Logo height={22} variant="dark" /><button onClick={() => setMobileNav(false)}><X size={20} className="text-slate-400" /></button></div>
            <NavItems onNavigate={() => setMobileNav(false)} />
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-xl sm:px-6" data-testid="member-shell-header">
          <button onClick={() => setMobileNav(true)} className="lg:hidden" data-testid="cust-mobile-menu"><Menu size={22} className="text-slate-500" /></button>
          <button onClick={() => setAskOpen(true)} data-testid="ask-mmt-ai-input"
            className="group flex h-10 max-w-xl flex-1 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-left text-[13px] text-slate-500 transition-colors hover:border-[#5B4BE6]/60">
            <Search size={15} className="text-slate-400 group-hover:text-[#5B4BE6]" />
            <span className="flex-1 truncate">Search or ask MMT anything…</span>
            <kbd className="hidden rounded-md border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-400 sm:inline">⌘K</kbd>
          </button>
          <div className="flex-1" />
          {planInfo.plan && (
            <button onClick={() => navigate("/billing")} data-testid="plan-badge"
              className={`hidden items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider sm:inline-flex ${PLAN_TONE[planInfo.plan] || PLAN_TONE.Preview}`}>
              <Crown size={11} /> {planInfo.plan} tier
            </button>
          )}
          <AccountMenu user={user} logout={logout} navigate={navigate}
            trigger={
              <button data-testid="cust-account-menu" className="flex items-center gap-2 rounded-full p-0.5 text-slate-400 transition-colors hover:text-[#5B4BE6]">
                <UserCircle2 size={28} strokeWidth={1.5} />
              </button>
            } />
        </header>
        <main className="relative flex-1 overflow-y-auto">
          {pastDue && (
            <div data-testid="past-due-banner" className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 sm:px-6">
              <div className="flex items-center gap-2 text-[13px] font-medium text-amber-700"><AlertTriangle size={16} /> Your last payment didn't go through. Update your card to keep your membership active.</div>
              <button onClick={() => navigate("/billing")} data-testid="past-due-fix" className="rounded-lg bg-amber-500 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-amber-600">Update payment</button>
            </div>
          )}
          <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8"><Outlet /></div>
          <button onClick={() => setAskOpen(true)} data-testid="ask-mmt-btn"
            className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 rounded-full bg-[#5B4BE6] px-5 py-3 font-head text-[13px] font-bold text-white shadow-[0_10px_30px_-8px_rgba(91,75,230,0.6)] transition-all hover:-translate-y-0.5 hover:bg-[#4F46E5]">
            <Sparkles size={16} /> Ask MMT
          </button>
        </main>
      </div>
      <AskModal open={askOpen} onClose={() => setAskOpen(false)} />
    </div>
  );
}

export function CustomerShell() {
  return (
    <DataProvider>
      <Inner />
    </DataProvider>
  );
}
